import axios, { AxiosResponse } from 'axios';
import {
  ChartResposnse,
  SparkJobStatuses,
  JobsListResponse,
  DetailedSparkJobDTO,
} from 'types/types';
import { API_URL } from 'utils/http';

type SchemasAndTablesData = { Schemas: string[]; Tables: string[] };
export async function getSchemasAndTables() {
  try {
    const res = await axios.get<SchemasAndTablesData>(`${API_URL}/data`);
    return res;
  } catch (error) {
    throw error;
  }
}

export async function getStatusCounts({
  clusterName,
}: {
  clusterName: string;
}) {
  try {
    type responseType = {
      Cancelled: number;
      Failed: number;
      In_Queue: number;
      Running: number;
      Succeeded: number;
      Timed_Out: number;
      Unknown: number;
    };
    const res = await axios.get<responseType>(
      `${API_URL}/status-counts?cluster_name=${clusterName}`
    );
    return res;
  } catch (error) {
    throw error;
  }
}

export async function getJobLogs({
  jobId,
  clusterName,
  mV,
  table,
  signature,
  expires,
  schema,
}: {
  jobId: string;
  clusterName: string;
  mV?: string;
  table?: string;
  signature?: string;
  expires?: string;
  schema?: string;
}) {
  try {
    return axios.get<string>(`${API_URL}/get-logs`, {
      params: {
        job_id: jobId,
        cluster_name: clusterName,
        ...(mV ? { mv: mV } : {}),
        ...(table ? { table } : {}),
        ...(signature ? { signature } : {}),
        ...(expires ? { expires } : {}),
        ...(schema ? { schema } : {}),
      },
    });
  } catch (error) {
    throw error;
  }
}

export async function getSignedURL({
  clusterName,
  tableName,
  schema,
  scope,
  jobId,
}: {
  clusterName: string;
  tableName: string;
  schema: string;
  scope: string;
  jobId?: string;
}) {
  type responseType = {
    signedURL: string;
  };
  try {
    return axios.get<responseType>(`${API_URL}/signed-url`, {
      params: {
        table: tableName,
        cluster_name: clusterName,
        schema,
        scope,
        job_id: jobId,
      },
    });
  } catch (error) {
    throw error;
  }
}

export async function getJobMetrics({ sparkJobId }: { sparkJobId: string }) {
  try {
    const res = await axios.get<Record<string, unknown>[]>(
      `${API_URL}/metrics`
    );
    return res;
  } catch (error) {
    throw error;
  }
}

export async function killJob({
  clusterName,
  sparkJobID,
}: {
  clusterName: string;
  sparkJobID: string;
}) {
  try {
    const res = await axios.post<AxiosResponse>(`${API_URL}/cancel-job`, {
      ClusterName: clusterName,
      job_id: sparkJobID,
    });
    return res;
  } catch (error) {
    throw error;
  }
}

export async function getChartJobsData({
  tableName,
  schemaName,
  clusterName,
  expires,
  signature,
  prefix,
  STATUS,
  CREATED_AT_DATE_RANGE,
}: {
  tableName: string;
  schemaName: string;
  clusterName: string;
  expires?: string;
  signature?: string;
  prefix?: string;
  STATUS?: SparkJobStatuses[];
  CREATED_AT_DATE_RANGE?: {
    start_date?: string;
    end_date?: string;
  };
}) {
  try {
    const res = await axios.get<ChartResposnse>(`${API_URL}/chart`, {
      params: {
        hard_limit: 20,
        table: tableName,
        schema: schemaName,
        cluster: clusterName,
        ...(expires ? { expires } : {}),
        ...(signature ? { signature } : {}),
        ...(prefix ? { prefix } : {}),
        ...(STATUS?.length ? { status: STATUS } : {}),
        ...(!!CREATED_AT_DATE_RANGE?.start_date
          ? { start_date: CREATED_AT_DATE_RANGE.start_date }
          : {}),
        ...(!!CREATED_AT_DATE_RANGE?.end_date
          ? { end_date: CREATED_AT_DATE_RANGE.end_date }
          : {}),
      },
      paramsSerializer: {
        indexes: null, // by default: false, Used to accept repeated same param key with different values (status)
      },
    });
    return res.data;
  } catch (error) {}
}

export async function getJobs({
  pageNumber,
  pageSize,
  sortBy,
  isSignedURL,
  sortOrder,
  status,
  tableName,
  schemaName,
  end_date,
  start_date,
  expires,
  signature,
  prefix,
  clusterName,
}: {
  pageNumber: number;
  pageSize: number;
  sortBy?: string | null;
  sortOrder?: string | null;
  tableName?: string | string[];
  schemaName?: string | string[];
  status?: string | string[];
  end_date?: string;
  start_date?: string;
  isSignedURL?: boolean;
  expires?: string;
  signature?: string;
  prefix?: string;
  clusterName?: string;
}) {
  try {
    const params = {
      page_number: pageNumber,
      page_size: pageSize,
      ...(sortBy ? { sort_by: sortBy } : {}),
      ...(sortOrder ? { sort_order: sortOrder } : {}),
      ...(tableName ? { table: tableName } : {}),
      ...(schemaName ? { schema: schemaName } : {}),
      ...(end_date ? { end_date } : {}),
      ...(status ? { status } : {}),
      ...(start_date ? { start_date } : {}),
      ...(isSignedURL ? { isSignedURL } : {}),
      ...(isSignedURL && expires ? { expires } : {}),
      ...(isSignedURL && signature ? { signature } : {}),
      ...(isSignedURL && prefix ? { prefix } : {}),
      ...(isSignedURL && clusterName ? { cluster_name: clusterName } : {}),
    };

    const res = await axios.get<JobsListResponse>(`${API_URL}/jobs`, {
      params,
      paramsSerializer: {
        indexes: null,
      },
    });
    return res;
  } catch (error) {
    throw error;
  }
}

export async function getJobDetail({
  jobId,
  schemaName,
  expires,
  signature,
  prefix,
  clusterName,
}: {
  jobId: string;
  schemaName?: string;
  expires?: string;
  signature?: string;
  prefix?: string;
  clusterName?: string;
}) {
  try {
    const res = await axios.get<DetailedSparkJobDTO>(
      `${API_URL}/jobs/${jobId}`,
      {
        params: {
          ...(clusterName ? { cluster_name: clusterName } : {}),
          ...(schemaName ? { schema: schemaName } : {}),
          ...(expires ? { expires } : {}),
          ...(signature ? { signature } : {}),
          ...(prefix ? { prefix } : {}),
        },
      }
    );
    return res;
  } catch (error) {
    throw error;
  }
}
